import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  ArrowRight, 
  MapPinCheck,
  MapPin,
  User
} from 'lucide-react';
import { GiHorseHead } from 'react-icons/gi';
import { getDocs, collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Fonction utilitaire pour calculer l'âge
const getAge = (dateNaissance) => {
  if (!dateNaissance) return null;
  const today = new Date();
  const birthDate = new Date(dateNaissance);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// Hook pour récupérer les couleurs de tags depuis Firebase
function useTagColors() {
  const [tagColors, setTagColors] = useState({});
  
  useEffect(() => {
    const loadTagColors = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'tagCouleursAnimaux'));
        const colors = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.id && data.codeHex) {
            colors[data.id] = data.codeHex;
          }
        });
        setTagColors(colors);
      } catch (error) {
        console.error('Erreur chargement couleurs tags:', error);
        // Couleurs par défaut si erreur
        setTagColors({
          1: '#2563eb', // Bleu
          2: '#ef4444', // Rouge
          3: '#22c55e', // Vert
          4: '#eab308', // Jaune
          5: '#f97316', // Orange
          6: '#8b5cf6', // Violet
          7: '#ec4899', // Rose
          8: '#06b6d4', // Turquoise
          9: '#6b7280', // Gris
          10: '#111827', // Noir
          11: '#92400e', // Marron
          12: '#f3f4f6'  // Blanc
        });
      }
    };
    
    loadTagColors();
  }, []);
  
  return tagColors;
}

// Hook pour charger le nom du propriétaire
function useProprietaireName(proprietaireId) {
  const [proprietaireName, setProprietaireName] = useState(null);
  
  useEffect(() => {
    const loadProprietaire = async () => {
      if (!proprietaireId) {
        setProprietaireName(null);
        return;
      }
      
      try {
        const proprietaireDoc = await getDoc(doc(db, 'proprietaires', proprietaireId));
        if (proprietaireDoc.exists()) {
          const data = proprietaireDoc.data();
          setProprietaireName(`${data.nom} ${data.prenom}`);
        }
      } catch (error) {
        console.error('Erreur chargement propriétaire:', error);
        setProprietaireName(null);
      }
    };
    
    loadProprietaire();
  }, [proprietaireId]);
  
  return proprietaireName;
}

const SimpleMoveConfirmationModal = ({ 
  isOpen, 
  onClose, 
  horse, 
  currentZone, 
  targetZone, 
  onConfirm 
}) => {
  const tagColors = useTagColors();
  const proprietaireName = useProprietaireName(horse?.proprietaire);
  
  if (!horse || !currentZone || !targetZone) return null;

  const age = getAge(horse.dateNaissance);
  const tagColorHex = tagColors[horse.tag] || '#e5e7eb';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPinCheck className="h-5 w-5 text-blue-600" />
            Confirmer le déplacement
          </DialogTitle>
          <DialogDescription>
            Vous êtes sur le point de déplacer un cheval d'une zone à une autre.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Informations du cheval */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div 
                  className="p-2 rounded-full"
                  style={{ 
                    backgroundColor: tagColorHex + '20' // Version transparente
                  }}
                >
                  <GiHorseHead 
                    className="h-6 w-6"
                    style={{ color: tagColorHex }}
                  />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-lg">{horse.nom || 'Sans nom'}</p>
                  <p className="text-sm text-gray-600">
                    {age !== null && <span>{age} ans</span>}
                    {age !== null && horse.couleur && <span> • </span>}
                    {horse.couleur && <span>{horse.couleur}</span>}
                    {(age !== null || horse.couleur) && horse.sexe && <span> • </span>}
                    {horse.sexe && <span>{horse.sexe}</span>}
                  </p>
                  {proprietaireName && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {proprietaireName}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Déplacement visuel */}
          <div className="flex items-center justify-between py-4">
            <div className="text-center flex-1">
              <div className="flex items-center justify-center mb-2">
                <div 
                  className="w-6 h-6 rounded-full border-2 border-white shadow-sm" 
                  style={{ backgroundColor: currentZone.color }}
                />
              </div>
              <p className="text-sm font-medium">{currentZone.name}</p>
              <p className="text-xs text-gray-500">{currentZone.type}</p>
            </div>
            
            <div className="px-4">
              <ArrowRight className="h-6 w-6 text-gray-400" />
            </div>
            
            <div className="text-center flex-1">
              <div className="flex items-center justify-center mb-2">
                <div 
                  className="w-6 h-6 rounded-full border-2 border-white shadow-sm" 
                  style={{ backgroundColor: targetZone.color }}
                />
              </div>
              <p className="text-sm font-medium">{targetZone.name}</p>
              <p className="text-xs text-gray-500">{targetZone.type}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button 
              onClick={onConfirm}
              className="flex items-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              Confirmer le déplacement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleMoveConfirmationModal;